import { useContext, useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Eye, Pencil, Download, Trash2, FileQuestion,
  Clock, CheckCircle, XCircle, AlertTriangle, Plus, CreditCard,
} from "lucide-react";
import Banner from "../component/Banner";
import BannerImg from "../image/gallaryBanner.png";
import Header from "../component/header";
import Footer from "../component/Footer";
import Viewsubmission from "../component/modals/Viewsubmission";
import Editsubmission from "../component/modals/Editsubmission";
import QuestForm from "../component/QuestForm";
import UpgradePrompt from "../component/access/UpgradePrompt";
import Toast from "../component/Toast";
import useAccess from "../hooks/useAccess";
import { UserDataContext } from "../context/User";
import { timeAgo } from "../utilis/Time";

// ── Status badge config ───────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending:  { label: "Pending",  Icon: Clock,         color: "bg-amber-100 text-amber-700 border-amber-200"  },
  approved: { label: "Approved", Icon: CheckCircle,   color: "bg-green-100 text-green-700 border-green-200"  },
  rejected: { label: "Rejected", Icon: XCircle,       color: "bg-red-100   text-red-700   border-red-200"    },
  deleted:  { label: "Deleted",  Icon: AlertTriangle, color: "bg-gray-100  text-gray-500  border-gray-200"   },
};


const PAYMENT_STATUS_CONFIG = {
  not_required: { label: "No payment", color: "bg-gray-100 text-gray-500 border-gray-200" },
  pending: { label: "Payment pending", color: "bg-amber-100 text-amber-700 border-amber-200" },
  paid: { label: "Paid", color: "bg-green-100 text-green-700 border-green-200" },
  failed: { label: "Payment failed", color: "bg-red-100 text-red-700 border-red-200" },
};

const PaymentBadge = ({ status }) => {
  const cfg = PAYMENT_STATUS_CONFIG[status || "not_required"] || PAYMENT_STATUS_CONFIG.not_required;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border font-outfit ${cfg.color}`}>
      <CreditCard size={11} strokeWidth={2.5} />
      {cfg.label}
    </span>
  );
};
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border font-outfit ${cfg.color}`}>
      <Icon size={11} strokeWidth={2.5} />
      {cfg.label}
    </span>
  );
};

const getSubmissionQuestTitle = (submission) => {
  if (submission?.challenge?.title) return submission.challenge.title;
  if (submission?.challengeTitle) return submission.challengeTitle;
  if (submission?.gallery?.title) return submission.gallery.title;
  return "General quest submission";
};

// ── Delete confirmation modal ─────────────────────────────────────────────────
const DeleteConfirm = ({ submission, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
        <Trash2 size={26} className="text-red-500" strokeWidth={1.5} />
      </div>
      <h3 className="font-cinzel text-lg font-bold text-gray-900 mb-2">Delete Submission?</h3>
      <p className="font-outfit text-sm text-gray-500 mb-6">
        Are you sure you want to delete <strong>"{submission?.title}"</strong>? This action cannot be undone.
      </p>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 py-2.5 rounded-full border border-gray-300 text-gray-600 font-outfit text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 py-2.5 rounded-full bg-red-500 text-white font-outfit text-sm font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <><Trash2 size={14} /> Delete</>
          )}
        </button>
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────
const Submission = () => {
  const { canSubmitQuest, tierKey, isFree, freeQuestLimitReached, questEntriesUsed, FREE_QUEST_LIMIT } = useAccess();
  const { fetchUser } = useContext(UserDataContext);
  const [searchParams] = useSearchParams();
  const initialChallengeId = searchParams.get("challenge") || "";

  const [submissions, setSubmissions]   = useState([]);
  const [challenges, setChallenges]     = useState([]);
  const [questPaymentSettings, setQuestPaymentSettings] = useState({ freeExtraSubmissionFeeCents: 1000, currency: "usd" });
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);

  const [viewItem, setViewItem]         = useState(null);
  const [editItem, setEditItem]         = useState(null);
  const [deleteItem, setDeleteItem]     = useState(null);
  const [deleting, setDeleting]         = useState(false);
  const [downloading, setDownloading]   = useState(null);
  const [retryingPayment, setRetryingPayment] = useState(null);

  const [toast, setToast] = useState({ type: "success", message: "" });
  const showToast = (type, message) => setToast({ type, message });
  const clearToast = () => setToast((t) => ({ ...t, message: "" }));

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/quest/get", { withCredentials: true });
      if (res.data.success) setSubmissions(res.data.quests || []);
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  useEffect(() => {
    const handlePaymentCancelled = async () => {
      const paymentState = searchParams.get("payment");
      const submissionId = searchParams.get("submission");
      if (paymentState !== "cancelled" || !submissionId) return;

      try {
        const res = await axios.post(`/api/quest/${submissionId}/payment-cancelled`, {}, { withCredentials: true });
        await fetchSubmissions();
        await fetchUser?.();
        showToast("error", res.data.message || "Payment was cancelled. You can retry payment from your submissions list.");
      } catch (err) {
        showToast("error", err?.response?.data?.message || "Payment was cancelled, but the submission status could not be updated.");
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
      }
    };

    handlePaymentCancelled();
  }, [searchParams, fetchSubmissions, fetchUser]);


  useEffect(() => {
    const fetchPaymentSettings = async () => {
      try {
        const res = await axios.get("/api/quest/payment-settings");
        if (res.data.success && res.data.settings) setQuestPaymentSettings(res.data.settings);
      } catch (err) {
        // Keep the default display amount if settings cannot be loaded.
      }
    };

    fetchPaymentSettings();
  }, []);

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const res = await axios.get("/api/challenges/get?limit=100");
        if (res.data.success) setChallenges(res.data.quest_challenges || []);
      } catch (err) {
        showToast("error", err?.response?.data?.message || "Failed to load quest events.");
      }
    };
    fetchChallenges();
  }, []);

  useEffect(() => {
    if (initialChallengeId && canSubmitQuest) {
      setShowForm(true);
    }
  }, [initialChallengeId, canSubmitQuest]);

  // ── Download ────────────────────────────────────────────────────────────────
  const handleDownload = async (id, title) => {
    setDownloading(id);
    try {
      const res = await axios.get(`/api/quest/${id}/download`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url  = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href  = url;
      link.download = title ? `${title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf` : `${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("success", "Submission downloaded successfully.");
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Download failed. Please try again.");
    } finally {
      setDownloading(null);
    }
  };


  const handleRetryPayment = async (submissionId) => {
    setRetryingPayment(submissionId);
    try {
      const res = await axios.post(`/api/quest/${submissionId}/retry-payment`, {}, { withCredentials: true });
      if (res.data.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
        return;
      }
      showToast("error", res.data.message || "Could not start payment. Please try again.");
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Could not start payment. Please try again.");
    } finally {
      setRetryingPayment(null);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      const res = await axios.post(`/api/quest/${deleteItem._id}/delete`, {}, { withCredentials: true });
      if (res.data.success) {
        setSubmissions((prev) => prev.filter((s) => s._id !== deleteItem._id));
        showToast("success", "Submission deleted successfully.");
        setDeleteItem(null);
      } else {
        showToast("error", res.data.message || "Failed to delete submission.");
      }
    } catch (err) {
      showToast("error", err?.response?.data?.message || "Failed to delete submission.");
    } finally {
      setDeleting(false);
    }
  };

  // ── After edit ──────────────────────────────────────────────────────────────
  const handleEditSuccess = () => {
    fetchSubmissions();
    showToast("success", "Submission updated successfully.");
  };

  // ── After new submission ────────────────────────────────────────────────────
  const handleSubmitSuccess = () => {
    fetchSubmissions();
    fetchUser?.();
    setShowForm(false);
    showToast("success", "Quest submitted successfully! It is now pending review.");
  };

  return (
    <div>
      <Header />
      <Banner
        backgroundImage={BannerImg}
        heading="Quest Submissions"
        paragraph="Track your quest entries, view feedback, and submit new work."
        minHeight="50vh"
        padding="80px 0"
      />

      <section className="main-lwbl bg-[#F9FAFB] py-16 px-4 rounded-[30px]">
        <div className="max-w-7xl mx-auto">

          {/* Free tier limit banner */}
          {/* {isFree && (
            <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3">
              <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle size={16} className="text-amber-600" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="font-outfit font-semibold text-amber-800 text-sm">
                  Free Member — Quest Entry Limit
                </p>
                <p className="font-outfit text-amber-700 text-xs mt-0.5">
                  You have used <strong>{questEntriesUsed}</strong> of <strong>{FREE_QUEST_LIMIT}</strong> free quest {FREE_QUEST_LIMIT === 1 ? "entry" : "entries"} this cycle.
                  {freeQuestLimitReached
                    ? ` Your included free entry has been used. Additional submissions require $${(questPaymentSettings.freeExtraSubmissionFeeCents / 100).toFixed(2)} payment.`
                    : " Submit your included free entry below."}
                </p>
                {freeQuestLimitReached && (
                  <a href="/lwbl/membership"
                    className="inline-flex items-center gap-1 mt-2 text-xs font-semibold font-outfit text-amber-800 underline hover:text-amber-900">
                    Upgrade Membership →
                  </a>
                )}
              </div>
            </div>
          )} */}

          {/* Toast */}
          {toast.message && (
            <div className="mb-6">
              <Toast type={toast.type} message={toast.message} onClose={clearToast} />
            </div>
          )}

          {/* Header row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="font-cinzel text-3xl md:text-4xl font-bold text-gray-900">
                My Submissions
              </h2>
              <p className="font-outfit text-gray-500 text-sm mt-1">
                {submissions.length} submission{submissions.length !== 1 ? "s" : ""} found
              </p>
            </div>
            {canSubmitQuest && (
              <button
                onClick={() => setShowForm((v) => !v)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit text-sm hover:scale-105 transition-transform shadow-md"
              >
                <Plus size={16} strokeWidth={2.5} />
                {showForm ? "Hide Form" : "New Submission"}
              </button>
            )}

          </div>

          {/* Submission Form */}
          {showForm && canSubmitQuest && (
            <div className="mb-10">
              <QuestForm
                challenges={challenges}
                initialChallengeId={initialChallengeId}
                onSuccess={handleSubmitSuccess}
                isFree={isFree}
                freeQuestLimitReached={freeQuestLimitReached}
                freeExtraSubmissionFeeCents={questPaymentSettings.freeExtraSubmissionFeeCents}
                onError={(msg) => {
                  fetchUser?.();
                  showToast("error", msg);
                }}
              />
            </div>
          )}

          {/* Submissions Table */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <FileQuestion size={30} className="text-gray-300" strokeWidth={1.5} />
              </div>
              <h3 className="font-cinzel text-xl font-bold text-gray-700 mb-2">No Submissions Yet</h3>
              <p className="font-outfit text-gray-400 text-sm max-w-sm mx-auto mb-6">
                You haven't submitted any quest entries yet. Start by clicking "New Submission" above.
              </p>
              {canSubmitQuest && (
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black font-semibold font-outfit text-sm hover:scale-105 transition-transform shadow"
                >
                  <Plus size={15} strokeWidth={2.5} />
                  Submit Your First Entry
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-6 py-4 font-outfit text-xs font-semibold text-gray-500 uppercase tracking-wider">Submission</th>
                      <th className="px-6 py-4 font-outfit text-xs font-semibold text-gray-500 uppercase tracking-wider">Quest / Event</th>
                      <th className="px-6 py-4 font-outfit text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-4 font-outfit text-xs font-semibold text-gray-500 uppercase tracking-wider">Review Status</th>
                      <th className="px-6 py-4 font-outfit text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment</th>
                      <th className="px-6 py-4 font-outfit text-xs font-semibold text-gray-500 uppercase tracking-wider">Submitted</th>
                      <th className="px-6 py-4 font-outfit text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {submissions.map((s) => (
                      <tr key={s._id} className="hover:bg-gray-50 transition-colors">
                        {/* Submission info */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {s.image ? (
                              <img src={s.image} alt={s.title}
                                className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-gray-100" />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <FileQuestion size={20} className="text-gray-300" />
                              </div>
                            )}
                            <div>
                              <p className="font-outfit font-semibold text-gray-900 text-sm">{s.title}</p>
                              {s.member_bio && (
                                <p className="font-outfit text-xs text-gray-400 mt-0.5 truncate max-w-[160px]">{s.member_bio}</p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Quest */}
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-outfit text-sm font-semibold text-gray-800 max-w-[190px] truncate">
                              {getSubmissionQuestTitle(s)}
                            </p>
                            {(s.challenge?.category || s.challengeCategory) && (
                              <p className="font-outfit text-xs text-gray-400 mt-0.5 capitalize">
                                {s.challenge?.category || s.challengeCategory}
                              </p>
                            )}
                          </div>
                        </td>

                        {/* Description */}
                        <td className="px-6 py-4 max-w-xs">
                          <p className="font-outfit text-sm text-gray-500 truncate">
                            {s.description || "—"}
                          </p>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <StatusBadge status={s.status} />
                        </td>

                        {/* Payment */}
                        <td className="px-6 py-4">
                          <PaymentBadge status={s.paymentStatus} />
                        </td>

                        {/* Time */}
                        <td className="px-6 py-4">
                          <span className="font-outfit text-xs text-gray-400">{timeAgo(s.createdAt)}</span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            {/* View */}
                            <button
                              onClick={() => setViewItem(s)}
                              title="View"
                              className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 flex items-center justify-center transition-colors"
                            >
                              <Eye size={15} className="text-blue-500" strokeWidth={2} />
                            </button>

                            {/* Edit */}
                            <button
                              onClick={() => setEditItem(s)}
                              title="Edit"
                              className="w-8 h-8 rounded-lg bg-amber-50 hover:bg-amber-100 flex items-center justify-center transition-colors"
                            >
                              <Pencil size={15} className="text-amber-500" strokeWidth={2} />
                            </button>


                            {(s.paymentStatus === "pending" || s.paymentStatus === "failed") && (
                              <button
                                onClick={() => handleRetryPayment(s._id)}
                                title="Retry Payment"
                                disabled={retryingPayment === s._id}
                                className="w-8 h-8 rounded-lg bg-cyan-50 hover:bg-cyan-100 flex items-center justify-center transition-colors disabled:opacity-50"
                              >
                                {retryingPayment === s._id ? (
                                  <div className="w-3.5 h-3.5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <CreditCard size={15} className="text-cyan-600" strokeWidth={2} />
                                )}
                              </button>
                            )}

                            {/* Download */}
                            <button
                              onClick={() => handleDownload(s._id, s.title)}
                              title="Download PDF"
                              disabled={downloading === s._id}
                              className="w-8 h-8 rounded-lg bg-green-50 hover:bg-green-100 flex items-center justify-center transition-colors disabled:opacity-50"
                            >
                              {downloading === s._id ? (
                                <div className="w-3.5 h-3.5 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Download size={15} className="text-green-500" strokeWidth={2} />
                              )}
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => setDeleteItem(s)}
                              title="Delete"
                              className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                            >
                              <Trash2 size={15} className="text-red-500" strokeWidth={2} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Upgrade prompt for non-wanderer users */}
          {!canSubmitQuest && (
            <div className="mt-10">
              <UpgradePrompt reason="tier" currentTier={tierKey} requiredTier="wanderer" />
            </div>
          )}

        </div>
      </section>

      {/* Modals */}
      <Viewsubmission
        isOpen={!!viewItem}
        submission={viewItem}
        onClose={() => setViewItem(null)}
      />
      <Editsubmission
        isOpen={!!editItem}
        submission={editItem}
        onClose={() => setEditItem(null)}
        onUpdate={handleEditSuccess}
      />
      {deleteItem && (
        <DeleteConfirm
          submission={deleteItem}
          onConfirm={handleDelete}
          onCancel={() => setDeleteItem(null)}
          loading={deleting}
        />
      )}

      <Footer />
    </div>
  );
};

export default Submission;
